import React, { memo } from 'react';
import AvatarGroup from '@atlaskit/avatar-group';
import Button from '@atlaskit/button/new';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { PromotionalCard } from '@atlassian/jira-home-common-ui/src/ui/promotional-card/index.tsx';
import Angie from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/people/assets/Angie.svg';
import Ed from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/people/assets/Ed.svg';
import More from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/people/assets/More.svg';
import Norah from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/people/assets/Norah.svg';
import Background from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/assets/generic-background.svg';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import type { Collaborators } from '../../../services/get-collaborators/types.tsx';
import { messages } from './messages.tsx';

type ActionsProps = {
	collaborators: Collaborators;
	onSubmit: () => unknown;
	onSnooze?: () => unknown;
	onOptOut?: () => unknown;
};

const Actions = ({ onSubmit, onSnooze, onOptOut, collaborators }: ActionsProps) => (
	<Inline space="space.050" alignBlock="center">
		<Button onClick={onSubmit}>
			<FormattedMessage
				{...messages.joinYourTeam}
				values={{
					collaborators: collaborators.length,
					collaborator: collaborators[0]?.name.split(' ')[0],
				}}
			/>
		</Button>
		{onSnooze && (
			<Button appearance="subtle" onClick={onSnooze}>
				<FormattedMessage {...messages.remindMeLater} />
			</Button>
		)}
		{onOptOut && (
			<Button appearance="subtle" onClick={onOptOut}>
				<FormattedMessage {...messages.notInterested} />
			</Button>
		)}
	</Inline>
);

type AvatarsProps = { collaborators: Collaborators };

const Avatars = ({ collaborators }: AvatarsProps) => {
	const data = [Norah, Angie, Ed, More].map((src) => ({ name: '', src }));

	for (const [i, user] of collaborators.entries()) {
		data[i] = {
			name: user.name,
			src: user.avatar,
		};
	}

	return (
		<Box xcss={AvatarWrapper}>
			<AvatarGroup
				appearance="stack"
				data={data}
				maxCount={4}
				size="large"
				borderColor="white"
				testId="cross-join-nudges.ui.your-work-widget.ui"
			/>
		</Box>
	);
};

type BaseProps = { onRender: () => unknown; onDismiss: () => unknown };
type Props = AvatarsProps & ActionsProps & BaseProps;

export const CrossJoinNudgeYourWorkCard = memo(
	({ onRender, collaborators, onDismiss, ...actionsProps }: Props) => {
		const { formatMessage } = useIntl();
		onRender();
		return (
			<Box xcss={Wrapper}>
				<Avatars collaborators={collaborators} />
				<PromotionalCard
					header={formatMessage(messages.header, {
						collaborators: collaborators.length,
						collaborator: collaborators[0]?.name,
					})}
					imageUrl={Background}
					imageAltText={formatMessage(messages.imageAltText)}
					description={formatMessage(messages.description)}
					primaryAction={<Actions {...actionsProps} collaborators={collaborators} />}
					reason={formatMessage(messages.tooltip)}
					analyticsSource="cross-join-nudges-your-work"
					onDismiss={onDismiss}
				/>
			</Box>
		);
	},
);

const AvatarWrapper = xcss({
	position: 'absolute',
	zIndex: 'card',
	left: '144px',
	top: '35px',
});

const Wrapper = xcss({
	display: 'inline-block',
	position: 'relative',
});
